/**
 * This file defines all supported languages for the application.
 */

export type TextDirection = 'ltr' | 'rtl';

export interface LanguageConfig {
  code: string;
  label: string;
  direction?: TextDirection;
  isProductionSupported: boolean;
  /** Speech SDK locale code (e.g., 'en-US') for Speech-to-Text */
  stt_code: string;
}

/**
 * Language select option type
 */
export interface LanguageSelectOption {
  label: string;
  value: string;
  isProductionSupported: boolean;
  stt_code: string;
}

export const DEFAULT_LANGUAGE_DIRECTION: TextDirection = 'ltr';

const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  { code: 'hi_IN', label: 'Hindi', isProductionSupported: true, stt_code: 'hi-IN' },
  { code: 'en_US', label: 'English', isProductionSupported: true, stt_code: 'en-US' },
  { code: 'fr_FR', label: 'French', isProductionSupported: true, stt_code: 'fr-FR' },
  { code: 'es_ES', label: 'Spanish', isProductionSupported: true, stt_code: 'es-ES' },
  { code: 'zh_CN', label: 'Chinese', isProductionSupported: true, stt_code: 'zh-CN' },
  { code: 'ko_KR', label: 'Korean', isProductionSupported: true, stt_code: 'ko-KR' },
  { code: 'ja_JP', label: 'Japanese', isProductionSupported: true, stt_code: 'ja-JP' },
  { code: 'de_DE', label: 'German', isProductionSupported: true, stt_code: 'de-DE' },
  { code: 'tr_TR', label: 'Turkish', isProductionSupported: true, stt_code: 'tr-TR' },
  { code: 'pt_PT', label: 'Portuguese', isProductionSupported: true, stt_code: 'pt-PT' },
  {
    code: 'ar_AR',
    label: 'Arabic',
    direction: 'rtl',
    isProductionSupported: true,
    stt_code: 'ar-AE',
  },
  { code: 'el_GR', label: 'Greek', isProductionSupported: true, stt_code: 'el-GR' },
  { code: 'it_IT', label: 'Italian', isProductionSupported: true, stt_code: 'it-IT' },
];

/**
 * Array of language codes only
 */
export const LANGUAGE_CODES: string[] = SUPPORTED_LANGUAGES.map((lang) => lang.code);

/**
 * Languages formatted for select dropdown
 */
export const LANGUAGE_SELECT_OPTIONS: LanguageSelectOption[] = SUPPORTED_LANGUAGES.map((lang) => ({
  label: lang.label,
  value: lang.code,
  isProductionSupported: lang.isProductionSupported,
  stt_code: lang.stt_code,
}));

/**
 * Derive the text direction for a given language code
 */
export const getLanguageDirection = (code?: string): TextDirection => {
  if (!code) return DEFAULT_LANGUAGE_DIRECTION;
  const language = SUPPORTED_LANGUAGES.find((lang) => lang.code === code);
  return language?.direction ?? DEFAULT_LANGUAGE_DIRECTION;
};

/**
 * `production` mode hides languages not yet signed off for prod (v3
 * `LanguageSwitcher` / v4 Display popover parity — both filter through this).
 */
export const filterLanguageOptionsForMode = (
  options: readonly LanguageSelectOption[],
  mode: string
): LanguageSelectOption[] =>
  mode === 'production' ? options.filter((option) => option.isProductionSupported) : [...options];
